import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation } from 'react-router-dom'
import { withStyles } from '@aura/components/styles'
import { Box, Grid } from '@aura/components'
import { useTranslation } from 'react-i18next'
import FetchStatus from '../../../../constants/fetch-forgotpwd'
import FetchAccountRecoveryStatus from '../../../../constants/fetch-account-recovery'
import ResendCodeTypes from '../../../../constants/resend-code'
import SubmitPasswordTypes from '../../../../constants/password'
import SubmitVerificationTypes from '../../../../constants/verification'
import { LOGIN_ROUTE } from '../../../../constants/routes'
import UserStatus from '../../../../constants/userStatus'
import ErrorPage from '../../../../components/error-page'
import AppBar from '../../../../components/app-bar-new'
import Footer from '../../../../components/new-footer'
import CustomLoader from '../../../../components/custom-loader'
import SessionTimeout from '../../../../components/session-timeout'
import SetPassword from '../password'
import RecoveryValidation from '../recoveryValidation'
import Welcome from '../welcome'
import RecoverySelection from '../recoverySelection'
import LogoutStatus from '../../../../constants/logout-status'
import Complete from '../success'
import { useConfig } from '../../../../utils/config'
import { clearCookies } from '../../../../utils/cookie'
import styles from './styles'


const Component = ({
    changeStep,
    classes,
    data,
    dataUserApps,
    errorMessage,
    init,
    initSendChallenge,
    initUserApps,
    onResendCode,
    onSubmitPassword,
    onVerifyAccountRecovery,
    resendSuccessMsg,
    sendChallengeStatus,
    sessionTimeout,
    status,
    stepNumber,
    submitStatusPassword,
    submitStatusResendCode,
    submitStatusVerification,
    submitStatusVerifyAuthenticatorStatus,
    userAppsStatus,
    userData,
    getAccountRecovery,
    accountRecoveryData,
    accountRecoveryStatus,
    onAccountRecoverySelection,
    logoutOktaSession,
    terminateOAGSession,
    logoutStatusOkta,
    logoutStatusOAG
}) => {
    const history = useHistory()
    const { t: translate, i18n } = useTranslation()
    const [ selectedRecoveryType, setSelectedRecoveryType ] = useState(null)
    const [ selectedRecoveryData, setSelectedRecoveryData ] = useState(null)
    const [ accountRecoveryNotActive, setAccountRecoveryNotActive ] = useState(false)
    const config = useConfig()
    const handleLogout = useCallback(() => {
        if (config?.logoutOAGSession) {
            terminateOAGSession(config?.logoutOAGSession)
        }
    }, [])

    useEffect(() => {
        if (config?.oktaCurrentSession) {
            logoutOktaSession(config?.oktaCurrentSession)
        }
    }, [])

    useEffect(() => {
        if (
            logoutStatusOkta === LogoutStatus.SUCCESS
    || logoutStatusOkta === LogoutStatus.ERROR
        ) {
            handleLogout()
        }
    }, [ logoutStatusOkta ])

    useEffect(() => {
        if (
            logoutStatusOAG === LogoutStatus.SUCCESS
    || logoutStatusOAG === LogoutStatus.ERROR
        ) {
            clearCookies(config?.cmxClean, 'OAG cookies')
            clearCookies(config?.secure2Clean, 'OAG secure2 cookies')
            clearCookies(config?.protectedClean, 'OAG protected cookies')
        }
    }, [ logoutStatusOAG ])


    const handleSelectType = useCallback(type => {
        const selectedItem = accountRecoveryData.find(item => item.type === type)
        setSelectedRecoveryType(type)
        setSelectedRecoveryData(selectedItem)
        onAccountRecoverySelection()
    }, [ accountRecoveryData, onAccountRecoverySelection ])

    const handleResendResetLink = useCallback(() => {
        history.push(LOGIN_ROUTE)
    }, [ history ])

    if (errorMessage === UserStatus.ACTIVE) {
        history.push(LOGIN_ROUTE)
    }

    // check for recovery token (from forgot password email)
    const location = useLocation()
    const params = new URLSearchParams(location.search)
    const recoveryToken = params.get('recoveryToken')
    const language = params.get('language')

    useEffect(() => {
        i18n.changeLanguage(language)
    }, [])

    const validateRecoveryToken = useCallback(() => {
        init(recoveryToken)
    }, [ init, recoveryToken ])

    useEffect(() => {
        if (stepNumber === 1 && accountRecoveryStatus === FetchAccountRecoveryStatus.SUCCESS) {
            Array.isArray(accountRecoveryData) && accountRecoveryData.length === 0 ? setAccountRecoveryNotActive(true) : setAccountRecoveryNotActive(false)
        }
    }, [ accountRecoveryStatus ])

    const getStepContent = step => {
        switch (step) {
            case 0:
                return <Welcome {...{
                    onNext: validateRecoveryToken
                }}/>
            case 1:
                return <RecoverySelection {...{
                    accountRecoveryData,
                    accountRecoveryStatus,
                    getAccountRecovery,
                    onSelectType: handleSelectType,
                    userData,
                }}/>
            case 2: 
                return <RecoveryValidation {...{
                    changeStep,
                    data,
                    error:errorMessage,
                    initSendChallenge,
                    isProcessing: submitStatusVerification === SubmitVerificationTypes.PENDING || submitStatusResendCode === ResendCodeTypes.PENDING,
                    isVerified: submitStatusVerification === SubmitVerificationTypes.SUCCESS,
                    onNext: onVerifyAccountRecovery,
                    onResendCode,
                    resendSuccessMsg,
                    selectedRecoveryType,
                    sendChallengeStatus,
                    verificationError : submitStatusVerification === SubmitVerificationTypes.ERROR
                }}/>
            case 3:
                return <SetPassword {...{
                    data: userData,
                    error: errorMessage,
                    isProcessing: submitStatusPassword === SubmitPasswordTypes.PENDING,
                    onNext: onSubmitPassword
                }} />
            case 4:
                return <Complete {...{
                    data: dataUserApps,
                    initUserApps,
                    userAppsStatus
                }} />
        }
    }

    if (sessionTimeout) {
        return <SessionTimeout />
    }

    
    return (
        <Box className={classes.pageWrapper}>
            <AppBar />
            <Box className={classes.contentWrapper}>            
                {(status === FetchStatus.PENDING
                    || submitStatusPassword === SubmitPasswordTypes.PENDING) ? (
                        <Box className={classes.gridContainerCenter}>
                            <Grid md={5} sm={8} xs={12}>
                                <CustomLoader
                                    label={translate('loading')}
                                    style={{ height: '20vh', marginBottom: '200px', marginTop: '200px' }}
                                />
                            </Grid>
                        </Box>
                    ) : accountRecoveryNotActive ? (
                        <ErrorPage errorTitle={translate('authenticationError')} errorMessage='ACCOUNT_RECOVERY_NOT_ACTIVE' />
                    ) : stepNumber === 0 && errorMessage ? (

                        <ErrorPage
                            errorMessage={errorMessage}
                            onResend={handleResendResetLink}
                        />

                    ) : (
                        <Grid className={classes.gridContainerCenter}>
                            {getStepContent(stepNumber)}
                        </Grid>
                    )}
            </Box>
            <Footer />
        </Box>
    )
}

Component.displayName = 'forgotPassword'

Component.defaultProps = {
    stepNumber: 0
}

Component.propTypes = {
    classes: PropTypes.shape({
        loader: PropTypes.string
    }),
    dataSendChallenge: PropTypes.object,
    dataUserApps: PropTypes.object,
    errorMessage: PropTypes.string,
    init: PropTypes.func.isRequired,
    initSendChallenge: PropTypes.func.isRequired,
    initUserApps: PropTypes.func.isRequired,
    onResendCode: PropTypes.func.isRequired,
    onSubmitPassword: PropTypes.func.isRequired,
    resendSuccessMsg: PropTypes.string,
    sendChallengeStatus: PropTypes.string,
    sessionTimeout: PropTypes.bool.isRequired,
    stepNumber: PropTypes.number,
    userAppsStatus: PropTypes.string.isRequired,
    userData: PropTypes.object.isRequired
}

export default withStyles(Component, styles)
